import {
  PHASE,
  TEAM_STATS_TABLES,
  POSITIONS,
  REMAINING_PLAYOFF_TEAMS_PHASES,
} from "../../../../common/constants.ts";
import { useLocalPartial } from "../util/local.ts";
import type { LocalStateUI } from "../../../../common/types.ts";
import { orderBy } from "../../../../common/utils.ts";
import { makeResponsiveDropdownOption } from "../../../../common/makeResponsiveDropdownOption.tsx";
import {
  translateTeamName,
  translateTeamRegion,
  translateTeamShortName,
} from "../util/teamNames.ts";
import { translatePosition } from "../util/positionNames.tsx";
import i18n from "../util/i18n.ts";
import type { DropdownOption, ResponsiveOption } from "../../../../common/types/dropdown.ts";

export type { DropdownOption, ResponsiveOption };

export const getSortedTeams = ({
  teamInfoCache,
  hideDisabledTeams,
}: {
  teamInfoCache: LocalStateUI["teamInfoCache"];
  hideDisabledTeams: boolean;
}) => {
  const array = [
    ...orderBy(
      teamInfoCache.filter((t) => !t.disabled),
      ["region", "name"],
    ),
  ];

  if (!hideDisabledTeams) {
    array.push(
      ...orderBy(
        teamInfoCache.filter((t) => t.disabled),
        ["region", "name"],
      ),
    );
  }

  const object: Record<string, string | ResponsiveOption[]> = {};
  for (const t of array) {
    const inactiveText = t.disabled
      ? i18n.t("dropdown.inactiveSuffix", {
          ns: "ui",
          defaultValue: " (inactive)",
        })
      : "";
    object[t.abbrev] = makeResponsiveDropdownOption(
      `${translateTeamShortName(t, t.abbrev)}${inactiveText}`,
      `${translateTeamRegion(t.region)} ${translateTeamName(t.name)}${inactiveText}`,
    );
  }

  return object;
};

const dropdownValues: Record<string, string | ResponsiveOption[]> = {
  special: "All-Star Game",
  "all|||teams": makeResponsiveDropdownOption("All", "All Teams"),
  watch: makeResponsiveDropdownOption("Follow", "Followed Players"),
  career: "Career",
  regularSeason: makeResponsiveDropdownOption("Reg Seas", "Regular Season"),
  playoffs: "Playoffs",
  "playoffs|||teams": makeResponsiveDropdownOption("Playoffs", "Remaining Playoff Teams"),
  "10": "Past 10 Seasons",
  "all|||seasons": makeResponsiveDropdownOption("All", "All Seasons"),
  perGame: makeResponsiveDropdownOption("Per G", "Per Game"),
  per36: makeResponsiveDropdownOption("Per 36", "Per 36 Minutes"),
  totals: "Totals",
  shotLocations: "Shot Locations and Highlights",
  advanced: "Advanced",
  gameHighs: "Game Highs",
  champion: "Won Championship",
  mvp: "Most Valuable Player",
  finals_mvp: "Finals MVP",
  sfmvp: "Semifinals MVP",
  dpoy: "Defensive Player of the Year",
  smoy: "Sixth Man of the Year",
  mip: "Most Improved Player",
  roy: "Rookie of the Year",
  first_team: "First Team All-League",
  second_team: "Second Team All-League",
  third_team: "Third Team All-League",
  all_league: "All-League",
  first_def: "First Team All-Defensive",
  second_def: "Second Team All-Defensive",
  third_def: "Third Team All-Defensive",
  all_def: "All-Defensive",
  all_star: "All-Star",
  all_star_mvp: "All-Star MVP",
  dunk: "Slam Dunk Contest Winner",
  three: "Three-Point Contest Winner",
  ppg_leader: "League Scoring Leader",
  rpg_leader: "League Rebounding Leader",
  apg_leader: "League Assists Leader",
  spg_leader: "League Steals Leader",
  bpg_leader: "League Blocks Leader",
  "all|||types": makeResponsiveDropdownOption("All", "All Types"),
  draft: "Draft",
  freeAgent: "FA Signed",
  reSigned: "Re-signed",
  release: "Released",
  trade: "Trades",
  team: "Team",
  opponent: makeResponsiveDropdownOption("Opp", "Opponent"),
  by_team: "By Team",
  by_conf: makeResponsiveDropdownOption("By Conf", "By Conference"),
  by_div: makeResponsiveDropdownOption("By Div", "By Division"),
  "all|||news": makeResponsiveDropdownOption("All", "All Stories"),
  normal: "Normal",
  big: "Only Big News",
  newest: "Newest First",
  oldest: "Oldest First",
  league: makeResponsiveDropdownOption("Leag", "League"),
  conf: makeResponsiveDropdownOption("Conf", "Conference"),
  your_teams: "Your Teams",
  combined: "Combined",
  current: "Current",
  overview: "Overview",
  available: "Available",
  signed: "Signed",
  both: "Both",
  draftPick: "Draft Picks",
  game: "Games",
  player: "Players",
  teamSeason: "Teams",
};

const translateDropdownText = (key: string, value: string, suffix?: string) => {
  const translationKey = suffix === undefined ? `dropdown.${key}` : `dropdown.${key}.${suffix}`;
  const translated = i18n.t(translationKey, {
    ns: "ui",
    defaultValue: value,
  });
  return typeof translated === "string" && translated.length > 0 ? translated : value;
};

const translateDropdownValue = (key: string, value: string | ResponsiveOption[]) => {
  if (Array.isArray(value)) {
    return value.map((option, i) => ({
      ...option,
      text: translateDropdownText(key, option.text, i === 0 ? "short" : "long"),
    }));
  }

  return translateDropdownText(key, value);
};

export const getDropdownValue = (
  key: number | string,
  sortedTeams: Record<string, string | ResponsiveOption[]>,
) => {
  if (typeof key === "number") {
    return String(key);
  }

  if (sortedTeams[key] !== undefined) {
    return sortedTeams[key];
  }

  if (dropdownValues[key] !== undefined) {
    return translateDropdownValue(key, dropdownValues[key]);
  }

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (TEAM_STATS_TABLES[key]) {
    const name = TEAM_STATS_TABLES[key].name;
    return i18n.t(`title.${name}`, { ns: "cols", defaultValue: name });
  }

  if (POSITIONS.includes(key)) {
    return translatePosition(key, "short");
  }

  return "???";
};

const useDropdownOptions = (field: string, customOptions?: DropdownOption[]) => {
  const state = useLocalPartial([
    "hideDisabledTeams",
    "phase",
    "season",
    "startingSeason",
    "teamInfoCache",
  ]);

  const sortedTeams = getSortedTeams(state);

  let keys: (number | string)[];

  if (customOptions) {
    return customOptions;
  } else if (field === "teams") {
    keys = Object.keys(sortedTeams);
  } else if (field === "teamsAndSpecial") {
    keys = ["special", ...Object.keys(sortedTeams)];
  } else if (field === "teamsAndAll") {
    keys = ["all|||teams", ...Object.keys(sortedTeams)];
  } else if (field === "teamsAndAllWatchPlayoffs") {
    keys = [
      "all|||teams",
      ...(REMAINING_PLAYOFF_TEAMS_PHASES.has(state.phase) ? ["playoffs|||teams"] : []),
      "watch",
      ...Object.keys(sortedTeams),
    ];
  } else if (field === "teamsAndYours") {
    keys = ["your_teams", ...Object.keys(sortedTeams)];
  } else if (
    field === "seasons" ||
    field === "seasonsAndCareer" ||
    field === "seasonsAndAll" ||
    field === "seasonsAndCurrent" ||
    field === "seasonsAndOldDrafts" ||
    field === "seasonsHistory" ||
    field === "seasonsFreeAgents"
  ) {
    keys = [];

    for (let season = state.season; season >= state.startingSeason; season--) {
      keys.push(season);
    }

    if (field === "seasonsAndCareer") {
      keys.unshift("career", "all|||seasons");
    }

    if (field === "seasonsAndAll") {
      keys.unshift("all|||seasons");
    }

    if (field === "seasonsAndCurrent") {
      keys.unshift("current");
    }

    if (field === "seasonsAndOldDrafts") {
      const NUM_PAST_SEASONS = 20; // Keep synced with league/create.js

      for (
        let season = state.startingSeason - 1;
        season >= state.startingSeason - NUM_PAST_SEASONS;
        season--
      ) {
        keys.push(season);
      }

      // Remove current season, if draft hasn't happened yet
      if (state.phase < PHASE.DRAFT) {
        keys.shift();
      }
    }

    if (field === "seasonsHistory") {
      // Remove current season until playoffs end
      if (state.phase <= PHASE.PLAYOFFS) {
        keys.shift();
      }
    }
  } else if (field === "playoffs") {
    keys = ["regularSeason", "playoffs"];
  } else if (field === "playoffsCombined") {
    keys = ["regularSeason", "playoffs", "combined"];
  } else if (field === "shows") {
    keys = ["10", "all|||seasons"];
  } else if (field === "statTypes" || field === "statTypesAdv") {
    keys = [
      "perGame",
      "per36",
      "totals",
      ...(field === "statTypesAdv" ? ["shotLocations", "advanced", "gameHighs"] : []),
    ];
  } else if (field === "statTypesStrict") {
    keys = ["perGame", "per36", "totals"];
  } else if (field === "eventType") {
    keys = ["all|||types", "draft", "freeAgent", "reSigned", "release", "trade"];
  } else if (field === "teamOpponent") {
    keys = ["team", "opponent"];
  } else if (field === "teamOpponentAdvanced") {
    keys = Object.keys(TEAM_STATS_TABLES);
  } else if (field === "teamAdvanced") {
    keys = Object.keys(TEAM_STATS_TABLES).filter(
      (key) => !key.includes("pponent") && !key.includes("opp"),
    );
  } else if (field === "newsLevels") {
    keys = ["big", "normal", "all|||news"];
  } else if (field === "newestOldestFirst") {
    keys = ["newest", "oldest"];
  } else if (field === "standingsType") {
    keys = ["league", "conf"];
  } else if (field === "typeFreeAgents") {
    keys = ["available", "signed", "both"];
  } else {
    throw new Error(`Unknown Dropdown field: ${field}`);
  }

  const newOptions: DropdownOption[] = keys.map((rawKey) => {
    const key =
      typeof rawKey === "string" && rawKey.includes("|||") ? rawKey.split("|||")[0]! : rawKey;
    return {
      key,
      value: getDropdownValue(rawKey, sortedTeams),
    };
  });

  return newOptions;
};

export default useDropdownOptions;
